// Recipe detail panel — slide-in side panel on desktop, bottom sheet
// on mobile. Cover + title + actions (Cook this / Add ingredients to
// shopping list / Delete) + ingredients + numbered steps + notes.
//
// Read mostly for Phase 2a. Editing ingredients / steps / notes
// lands in Phase 2b alongside the full New Recipe editor.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon, hueGrad } from "../../icons";
import { api, RECIPE_TAG_GROUPS, type Recipe, type RecipeItem, type RecipeSeasoning } from "../../lib/rpc";
import type { NotifyFn } from "../../components/toast";
import { hueFromId } from "../../lib/use-recipes";
import { usePantry } from "../../lib/use-pantry";
import { sse } from "../../lib/sse";
import { scaleQuantity } from "../../lib/recipe-scale";
import { ListPicker } from "./list-picker";
import { SeasoningsPicker } from "./seasonings-picker";

const SCALE_OPTIONS = [0.5, 1, 1.5, 2, 3, 4];

interface RecipeDetailProps {
  recipeId: string | null;
  scale: number;
  onScaleChange: (scale: number) => void;
  onClose: () => void;
  onCook: (recipeId: string) => void;
  onDelete: (recipeId: string) => void;
  onEdit: (recipeId: string) => void;
  onUpdate: (id: string, patch: Partial<Pick<Recipe, "name" | "imageUrl" | "instructions" | "notes">>) => Promise<Recipe | null>;
  onSetTags: (id: string, tags: string[]) => Promise<Recipe | null>;
  onRetag: (id: string) => Promise<void>;
  notify: NotifyFn;
  /** User household preference. With recipe.serves, drives the auto-
   *  scale chip (auto = household / recipe.serves, applied as the
   *  initial scale on open if the user hasn't already picked one). */
  householdSize?: number | null;
}

export function RecipeDetail({
  recipeId, scale, onScaleChange, onClose, onCook, onDelete, onEdit, onUpdate: _onUpdate, onSetTags, onRetag, notify,
  householdSize = null,
}: RecipeDetailProps) {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [items, setItems] = useState<RecipeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  // Seasonings this recipe calls for + the household's stock state, fetched
  // alongside the recipe. Drives the seasonings picker step in the add flow.
  const [seasoningsInfo, setSeasoningsInfo] = useState<{ configured: boolean; seasonings: RecipeSeasoning[] }>(
    { configured: false, seasonings: [] },
  );
  const [seasoningsPickerOpen, setSeasoningsPickerOpen] = useState(false);
  // The list chosen in ListPicker, held while the seasonings picker decides.
  const [pendingList, setPendingList] = useState<{ id: number; name: string } | null>(null);

  // Swipe-down-to-close gesture. The bottom-sheet variant of the
  // panel (mobile, ≤760px) is the primary target — the tap-to-close
  // backdrop is hard to hit there since the sheet covers 92% of the
  // viewport. Drag down to dismiss.
  //
  // Guards:
  //  - Only initiates when the panel itself is scrolled to top
  //    (otherwise let the inner scroll continue — long instructions /
  //    ingredients aren't blocked by the gesture).
  //  - Only down-drags count; up-drags fall through to native scroll.
  //  - Committed-threshold of 8px so a quick tap doesn't visibly nudge
  //    the panel before the touch handler can see it's not a swipe.
  //
  // Threshold to close: 120px OR (60px+ with a flick velocity). Below
  // that the panel snaps back to 0 with a quick ease-out.
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    let startY = 0;
    let startedAtTop = false;
    let committed = false;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;  // px / ms
    const COMMIT_PX = 8;
    const CLOSE_PX = 120;
    const FLICK_PX = 60;
    const FLICK_VEL = 0.5;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      startY = t.clientY;
      lastY = startY;
      lastT = performance.now();
      startedAtTop = el.scrollTop <= 0;
      committed = false;
      velocity = 0;
      // Strip any pending transition from the previous gesture so the
      // panel responds to drag immediately, not via an animation tween.
      el.style.transition = "";
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !startedAtTop) return;
      const dy = t.clientY - startY;
      if (dy <= 0) return;  // upward — let scroll happen
      if (!committed) {
        if (dy < COMMIT_PX) return;
        committed = true;
      }
      // Track velocity for flick detection on release.
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      velocity = (t.clientY - lastY) / dt;
      lastY = t.clientY;
      lastT = now;
      // Apply the translate directly — bypasses Preact's render cycle
      // so 60fps stays smooth even on lower-end Android WebViews.
      el.style.transform = `translateY(${dy}px)`;
      // Dim the backdrop proportionally for visual continuity.
      const bg = el.parentElement;
      if (bg) bg.style.opacity = String(Math.max(0, 1 - dy / 400));
      e.preventDefault();
    };

    const onTouchEnd = () => {
      if (!committed) {
        // Reset any partial state (e.g. velocity tracker but never
        // crossed the commit threshold).
        return;
      }
      committed = false;
      const matched = parseFloat(el.style.transform.replace(/[^\d.-]/g, "")) || 0;
      const shouldClose =
        matched > CLOSE_PX
        || (matched > FLICK_PX && velocity > FLICK_VEL);
      el.style.transition = "transform 200ms cubic-bezier(.4,0,.2,1)";
      const bg = el.parentElement;
      if (bg) bg.style.transition = "opacity 200ms ease-out";
      if (shouldClose) {
        el.style.transform = `translateY(100vh)`;
        if (bg) bg.style.opacity = "0";
        window.setTimeout(() => onClose(), 195);
      } else {
        el.style.transform = "";
        if (bg) bg.style.opacity = "";
      }
    };

    // `{ passive: false }` so onTouchMove can preventDefault the
    // browser's pull-to-refresh / overscroll behavior during a drag.
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true });
    el.addEventListener("touchcancel", onTouchEnd,  { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onClose]);
  const [editingTags, setEditingTags] = useState(false);
  // Whether this recipe belongs to the viewer's household. Catalogue / other-
  // household recipes are read-only (tags can't be edited). Defaults true —
  // the page only opens owned recipes today, but recipes.get is authoritative.
  const [owned, setOwned] = useState(true);
  const [retagging, setRetagging] = useState(false);
  // Pantry state — drives the "Running low" banner above the
  // ingredients list. Costs one RPC call on first open of the recipe
  // detail across the user's session (use-pantry caches via IDB);
  // subsequent opens read from cache without a network hop.
  const pantry = usePantry();
  // Ingredients in this recipe that the user has in pantry but
  // flagged as running low. Surfaced via a banner so the user can
  // one-tap restock them before/during cooking — the bulk
  // "Add ingredients" flow's pantry-gate keeps lowStock items on the
  // shopping list path, but this banner makes it visible BEFORE the
  // user clicks add. Match is case-insensitive whole-string equality
  // — fuzzier matching (substring / fuzz) is something to revisit
  // once we see real-world misses.
  const lowStockMatches = useMemo(() => {
    if (items.length === 0 || pantry.items.length === 0) return [];
    const byName = new Map<string, typeof pantry.items[number]>();
    for (const p of pantry.items) byName.set(p.name.toLowerCase().trim(), p);
    const matches: { item: RecipeItem; pantryItem: typeof pantry.items[number] }[] = [];
    for (const it of items) {
      const key = (it.text || "").toLowerCase().trim();
      const p = byName.get(key);
      if (p && p.lowStock) matches.push({ item: it, pantryItem: p });
    }
    return matches;
  }, [items, pantry.items]);
  // Once the user picks a chip we stop auto-applying — otherwise the
  // detail panel would fight the user every time household / serves
  // updates from somewhere else (settings, SSE update, etc.).
  const [userPickedScale, setUserPickedScale] = useState(false);

  useEffect(() => {
    // Reset transient UI state whenever the panel switches recipes (or
    // closes). The component returns null when recipeId is null but
    // stays mounted, so without this reset Edit / Re-tag state leaks
    // across opens of different recipes.
    setEditingTags(false);
    setRetagging(false);
    setOwned(true);
    setUserPickedScale(false);
    setSeasoningsInfo({ configured: false, seasonings: [] });
    if (!recipeId) {
      setRecipe(null);
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.recipesGet(recipeId)
      .then((r) => {
        if (cancelled) return;
        setRecipe(r.recipe);
        setItems(r.items);
        setOwned(r.owned);
      })
      .catch(() => { /* parent's error handler covers */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Seasonings split — best-effort, independent of the recipe load.
    // Failure (e.g. offline) just leaves the add flow seasoning-free.
    api.recipesSeasoningsFor(recipeId)
      .then((s) => { if (!cancelled) setSeasoningsInfo(s); })
      .catch(() => { /* add flow falls back to no-seasonings */ });
    return () => { cancelled = true; };
  }, [recipeId]);

  // Computed auto-scale factor. Null when either piece is missing —
  // chip row falls back to "× whatever the user picked" / 1x default.
  // Rounded to 2 decimal places so a household of 5 + recipe-serves
  // of 3 renders as ×1.67 rather than ×1.6666666666666667.
  const autoScale = (recipe?.serves && householdSize && recipe.serves > 0)
    ? Math.round((householdSize / recipe.serves) * 100) / 100
    : null;

  // Apply auto-scale on initial load (or when householdSize updates
  // before the user has manually picked a chip). Guarded by
  // userPickedScale so we don't keep stomping their override.
  useEffect(() => {
    if (autoScale == null) return;
    if (userPickedScale) return;
    if (autoScale === scale) return;
    onScaleChange(autoScale);
    // intentionally NOT setting userPickedScale here — this is the
    // system's pick, not the user's. They can still tap a preset
    // chip and it'll mark userPickedScale=true.
  }, [autoScale, userPickedScale]);

  useEffect(() => {
    if (!recipeId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [recipeId, onClose]);

  // Keep our local state synced when the classifier finishes
  // (recipe:tags.updated) or any field is edited (recipe:updated, also
  // covers another-device edits) or an item is added/removed/edited
  // (recipe:item.*). Without these the detail panel keeps stale data
  // after the user clicks Edit -> Save in the modal because activeId
  // didn't change, so the panel's recipeId effect doesn't refetch.
  useEffect(() => {
    if (!recipeId) return;
    return sse.on((ev: any) => {
      if (!ev) return;
      // recipe:updated carries { recipe } only; everything else has
      // a top-level recipeId. Normalize before filtering.
      const evRecipeId = ev.recipeId ?? ev.recipe?.id;
      if (String(evRecipeId) !== String(recipeId)) return;
      switch (ev.type) {
        case "recipe:tags.updated":
          setRecipe((prev) => prev ? { ...prev, tags: ev.tags || null } : prev);
          setRetagging(false);
          break;
        case "recipe:updated":
          if (ev.recipe) setRecipe(ev.recipe);
          break;
        case "recipe:item.added":
          if (ev.item) setItems((prev) => [...prev, ev.item]);
          break;
        case "recipe:item.updated":
          if (ev.item) setItems((prev) => prev.map((i) => i.id === ev.item.id ? ev.item : i));
          break;
        case "recipe:item.deleted":
          if (ev.itemId) setItems((prev) => prev.filter((i) => String(i.id) !== String(ev.itemId)));
          break;
      }
    });
  }, [recipeId]);

  if (!recipeId) return null;

  // "Add ingredients" — opens a list picker; on pick, server-side
  // recipes.addToList applies the current scale and bulk-inserts.
  // List picker handles last-used persistence.
  //
  // Pantry-gate: by default the server skips ingredients the user
  // already has in pantry (with healthy stock + not expiring soon).
  // When that filter applied, the toast surfaces "Skipped N in pantry"
  // with an "Add anyway" action that re-runs the same call with
  // usePantry:false to commit the override.
  const [pickerOpen, setPickerOpen] = useState(false);
  // Separate picker state for the "Add low-stock to shopping" path —
  // sharing pickerOpen with the bulk Add flow would make the picker's
  // selection handler ambiguous about which subset to commit.
  const [lowStockPickerOpen, setLowStockPickerOpen] = useState(false);

  // Add just the lowStock-matched ingredients to a shopping list.
  // Reuses shoppingAddItem in a loop so we don't need a new server
  // endpoint. Each item carries the recipe's category/emoji hint to
  // keep the classifier from re-running for known-good rows.
  const handleAddLowStockToList = async (listId: number, listName: string) => {
    const targets = lowStockMatches;
    if (targets.length === 0) return;
    setAdding(true);
    setLowStockPickerOpen(false);
    try {
      const results = await Promise.allSettled(
        targets.map(({ item }) => api.shoppingAddItem(listId, item.text, item.quantity || undefined)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      const tail = failed > 0 ? ` · ${failed} failed` : "";
      notify(`Restock list updated — added ${ok} to ${listName}${tail}`);
    } catch (e: any) {
      notify(e?.message || "couldn't add restock items");
    } finally {
      setAdding(false);
    }
  };

  // List chosen in ListPicker. If the recipe has spice-rack seasonings,
  // detour through the seasonings picker so the user can opt in to the
  // ones they're out of; otherwise commit straight away (no seasonings).
  const handleListPicked = (listId: number, listName: string) => {
    setPickerOpen(false);
    if (seasoningsInfo.seasonings.length > 0) {
      setPendingList({ id: listId, name: listName });
      setSeasoningsPickerOpen(true);
    } else {
      doAddToList(listId, listName, []);
    }
  };

  const doAddToList = async (
    listId: number,
    listName: string,
    includeSeasonings: string[],
    opts?: { usePantry?: boolean },
  ) => {
    setAdding(true);
    try {
      const r = await api.recipesAddToList(recipeId, listId, { scale, usePantry: opts?.usePantry, includeSeasonings });
      const scaleSuffix = scale !== 1 ? ` (×${scale})` : "";
      const seasoningSuffix = r.seasoningsAdded ? ` · +${r.seasoningsAdded} seasoning${r.seasoningsAdded === 1 ? "" : "s"}` : "";
      const addedLine = r.added.length === 0
        ? (r.seasoningsAdded ? `Added ${r.seasoningsAdded} seasoning${r.seasoningsAdded === 1 ? "" : "s"} to ${listName}` : "Nothing added")
        : `Added ${r.added.length} ingredient${r.added.length === 1 ? "" : "s"} to ${listName}${scaleSuffix}${seasoningSuffix}`;
      if (r.pantrySkipped.length > 0 && opts?.usePantry !== false) {
        // Surface the skip as an actionable toast — clicking "Add
        // anyway" re-runs the same call with the gate disabled. The
        // seasoning choices are preserved across the override.
        const skippedNames = r.pantrySkipped.map((s) => s.text).slice(0, 3).join(", ");
        const more = r.pantrySkipped.length > 3 ? ` +${r.pantrySkipped.length - 3} more` : "";
        notify(
          `${addedLine} · Skipped ${r.pantrySkipped.length} in pantry: ${skippedNames}${more}`,
          { action: { label: "Add anyway", onClick: () => doAddToList(listId, listName, includeSeasonings, { usePantry: false }) } },
        );
      } else {
        notify(addedLine);
      }
    } catch (e: any) {
      notify(e?.message || "couldn't add ingredients");
    } finally {
      setAdding(false);
      setSeasoningsPickerOpen(false);
      setPendingList(null);
    }
  };

  const steps = Array.isArray(recipe?.instructions) ? recipe!.instructions : [];

  return (
    // Fragment so the ListPicker is a SIBLING of recipe-detail-bg,
    // not a child. Otherwise the picker is stuck inside the panel's
    // stacking context (recipe-detail-bg has z-index, which creates
    // one) and its higher z-index can't lift it above the panel.
    <>
    <div className="recipe-detail-bg" onClick={onClose}>
      <aside
        ref={panelRef}
        className="recipe-detail"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={recipe?.name || "Recipe"}
      >
        {/* Visual drag-handle — signals "this can be swiped down to
         *  close" on mobile. Hidden ≥760px where the panel is a slide-
         *  in side panel instead of a bottom sheet. The aside-level
         *  touch handlers in the effect above own the actual gesture. */}
        <div className="recipe-detail-drag-handle" aria-hidden />
        <div
          className="recipe-detail-cover"
          style={recipe?.imageUrl ? undefined : { background: hueGrad(hueFromId(recipeId)) }}
        >
          {recipe?.imageUrl && <img src={recipe.imageUrl} alt={recipe.name} />}
        </div>

        <div className="recipe-detail-head">
          <div className="recipe-detail-title-row">
            <h2 className="recipe-detail-title">{recipe?.name || "Loading…"}</h2>
            <button className="icon-btn" onClick={onClose} title="Close" type="button">
              <Icon name="x" size={14} />
            </button>
          </div>
          <div className="recipe-detail-stats">
            <span>{items.length} ingredient{items.length === 1 ? "" : "s"}</span>
            {steps.length > 0 && (
              <>
                <span>·</span>
                <span>{steps.length} step{steps.length === 1 ? "" : "s"}</span>
              </>
            )}
            {recipe?.serves
              ? (<><span>·</span><span>serves {recipe.serves}</span></>)
              : recipe && (
                  <>
                    <span>·</span>
                    <button
                      className="recipe-detail-set-serves"
                      type="button"
                      onClick={() => onEdit(recipeId)}
                      title="Set servings so auto-scale knows how many it feeds"
                    >
                      Set servings
                    </button>
                  </>
                )}
          </div>
          {/* Multiplier — scales displayed quantities and the qty sent
              to shopping when "Add ingredients" is used. Resets when
              the user closes the panel. When the user has a household
              size AND the recipe has a serves count, autoScale fills
              the gap and gets surfaced as an extra "auto" chip. */}
          <ScaleChips
            value={scale}
            onChange={(s) => { setUserPickedScale(true); onScaleChange(s); }}
            auto={autoScale}
            householdSize={householdSize}
          />
        </div>

        <div className="recipe-detail-actions">
          <button
            className="btn btn-primary"
            onClick={() => onCook(recipeId)}
            disabled={steps.length === 0}
            title={steps.length === 0 ? "Add steps before cooking" : "Cook this"}
            type="button"
          >
            <Icon name="flame" size={14} />
            <span>Cook this</span>
          </button>
          <button
            className="btn"
            onClick={() => setPickerOpen(true)}
            disabled={adding || items.length === 0}
            type="button"
          >
            <Icon name="plus" size={14} />
            <span>{adding ? "Adding…" : "Add ingredients"}</span>
          </button>
          <button
            className="btn"
            onClick={() => onEdit(recipeId)}
            title="Edit recipe"
            type="button"
          >
            <Icon name="edit" size={14} />
          </button>
          <button
            className="btn"
            onClick={() => onDelete(recipeId)}
            title="Delete recipe"
            type="button"
          >
            <Icon name="trash" size={14} />
          </button>
        </div>

        {loading && (
          <div className="recipe-detail-section">
            <p style={{ color: "var(--text-dim)", fontSize: 12 }}>loading…</p>
          </div>
        )}

        {!loading && (
          <>
            <section className="recipe-detail-section">
              <div className="recipe-detail-tags-head">
                <div className="recipe-detail-section-label">Tags</div>
                {/* Tag editing is owner-only — a catalogue / other-household
                    recipe shows its tags read-only (no Edit / Re-tag). */}
                {owned && (
                  <div className="recipe-detail-tags-actions">
                    <button
                      className="link-btn"
                      onClick={() => setEditingTags((v) => !v)}
                      type="button"
                    >
                      {editingTags ? "Done" : "Edit"}
                    </button>
                    <button
                      className="link-btn"
                      onClick={async () => {
                        if (retagging) return;
                        setRetagging(true);
                        try {
                          await onRetag(recipeId);
                          notify("Re-tagging…");
                        } catch {
                          setRetagging(false);
                        }
                      }}
                      disabled={retagging}
                      title="Wipe and re-run the auto-classifier"
                      type="button"
                    >
                      {retagging ? "Re-tagging…" : "Re-tag"}
                    </button>
                  </div>
                )}
              </div>
              <RecipeTagEditor
                recipe={recipe}
                editing={owned && editingTags}
                onChange={(tags) => {
                  setRecipe((prev) => prev ? { ...prev, tags } : prev);
                  onSetTags(recipeId, tags).catch(() => {});
                }}
              />
            </section>

            {/* Running-low banner — surfaces pantry items flagged as
             *  lowStock that this recipe uses, with a single tap to add
             *  them all to a shopping list. Hidden when nothing matches
             *  so it doesn't take up space on a routine cook. */}
            {lowStockMatches.length > 0 && (
              <RecipeLowStockBanner
                matches={lowStockMatches}
                disabled={adding}
                onAdd={() => {
                  // Reuse the existing list picker — same UX as the
                  // bulk Add ingredients flow. The picker calls back
                  // with the chosen list, then we add only the
                  // low-stock subset (not the whole ingredient list).
                  setLowStockPickerOpen(true);
                }}
              />
            )}

            <section className="recipe-detail-section">
              <div className="recipe-detail-section-label">Ingredients</div>
              {items.length === 0 ? (
                <p style={{ color: "var(--text-dim)", fontStyle: "italic", margin: 0 }}>
                  No ingredients yet.
                </p>
              ) : (
                <div className="recipe-detail-ingredients">
                  {items.map((it) => {
                    const qty = scaleQuantity(it.quantity, scale);
                    return (
                      <div key={it.id} className="recipe-detail-ingredient">
                        {it.emoji && <span style={{ width: 18 }}>{it.emoji}</span>}
                        <span className="recipe-detail-ingredient-text">{it.text}</span>
                        {qty && (
                          <span className="recipe-detail-ingredient-qty">{qty}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {steps.length > 0 && (
              <section className="recipe-detail-section">
                <div className="recipe-detail-section-label">Steps</div>
                <div className="recipe-detail-steps">
                  {steps.map((step, i) => (
                    <div key={i} className="recipe-detail-step">
                      <span className="recipe-detail-step-num" aria-hidden />
                      <div className="recipe-detail-step-body">{step}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {recipe?.notes && (
              <section className="recipe-detail-section">
                <div className="recipe-detail-section-label">Notes</div>
                <p className="recipe-detail-notes">{recipe.notes}</p>
              </section>
            )}
          </>
        )}
      </aside>
    </div>

    <ListPicker
      open={pickerOpen}
      scale={scale}
      onClose={() => setPickerOpen(false)}
      onPick={handleListPicked}
      notify={notify}
    />
    {/* Seasonings step — only reached when the recipe calls for spice-rack
     *  items. Lets the user opt in to the ones they're out of before the
     *  groceries land. */}
    {pendingList && (
      <SeasoningsPicker
        open={seasoningsPickerOpen}
        listName={pendingList.name}
        seasonings={seasoningsInfo.seasonings}
        configured={seasoningsInfo.configured}
        onClose={() => { setSeasoningsPickerOpen(false); setPendingList(null); }}
        onConfirm={(include) => doAddToList(pendingList.id, pendingList.name, include)}
      />
    )}
    {/* Separate picker for the lowStock-banner's "Add to shopping"
     *  action so its onPick targets the subset, not the whole recipe. */}
    <ListPicker
      open={lowStockPickerOpen}
      scale={1}
      onClose={() => setLowStockPickerOpen(false)}
      onPick={handleAddLowStockToList}
      notify={notify}
    />
    </>
  );
}

/** Banner shown above the ingredients list when one or more recipe
 *  ingredients are in pantry but flagged lowStock. Single CTA opens a
 *  list picker; on pick, only the lowStock subset is committed.
 *  Compact label so it fits inline above the section without dominating. */
function RecipeLowStockBanner({
  matches, disabled, onAdd,
}: {
  matches: { item: RecipeItem; pantryItem: { name: string; qty: string } }[];
  disabled: boolean;
  onAdd: () => void;
}) {
  // Cap the visible list at 4 so a multi-mention banner doesn't sprawl.
  const visible = matches.slice(0, 4).map((m) => m.item.text);
  const more = matches.length - visible.length;
  return (
    <div className="recipe-lowstock-banner">
      <div className="recipe-lowstock-banner-icon" aria-hidden>
        <Icon name="warn" size={13} />
      </div>
      <div className="recipe-lowstock-banner-body">
        <div className="recipe-lowstock-banner-title">Running low in pantry</div>
        <div className="recipe-lowstock-banner-list">
          {visible.join(", ")}{more > 0 ? ` · +${more} more` : ""}
        </div>
      </div>
      <button
        className="btn btn-primary recipe-lowstock-banner-cta"
        onClick={onAdd}
        disabled={disabled}
        type="button"
      >
        Add to shopping
      </button>
    </div>
  );
}

// Multiplier chips — preset factors plus an optional auto chip that
// shows "×<computed>  feeds N" when the user has set a household size
// AND the recipe has a serves value. The auto chip is rendered ahead
// of the presets so it's the leftmost / first thing the eye lands on,
// since that's typically the value the user wants. Auto value is
// hidden when it lines up with an existing preset chip (no need to
// double it up).
function ScaleChips({ value, onChange, auto, householdSize }: {
  value: number;
  onChange: (s: number) => void;
  auto?: number | null;
  householdSize?: number | null;
}) {
  const autoMatchesPreset = auto != null && SCALE_OPTIONS.some((s) => s === auto);
  return (
    <div className="recipe-detail-scale" role="radiogroup" aria-label="Scale">
      {auto != null && !autoMatchesPreset && (
        <button
          type="button"
          role="radio"
          aria-checked={value === auto}
          className={`recipe-detail-scale-chip auto${value === auto ? " active" : ""}`}
          onClick={() => onChange(auto)}
          title={householdSize ? `Feeds ${householdSize}` : undefined}
        >
          ×{auto}{householdSize ? ` · feeds ${householdSize}` : ""}
        </button>
      )}
      {SCALE_OPTIONS.map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          className={`recipe-detail-scale-chip${value === s ? " active" : ""}${auto === s ? " is-auto" : ""}`}
          onClick={() => onChange(s)}
        >
          ×{s}{auto === s && householdSize ? ` · feeds ${householdSize}` : ""}
        </button>
      ))}
    </div>
  );
}

// Tag editor: read-only chip list when collapsed, expandable picker
// when `editing=true`. Hard-caps at 5 selected (matches server-side
// cap in recipes.setTags). Each row corresponds to one taxonomy
// dimension (time/effort, cuisine, dish-type) for visual grouping
// only — the storage stays flat.
function RecipeTagEditor({
  recipe, editing, onChange,
}: {
  recipe: Recipe | null;
  editing: boolean;
  onChange: (tags: string[]) => void;
}) {
  const tags = recipe?.tags || [];
  if (!editing) {
    if (tags.length === 0) {
      return (
        <p style={{ color: "var(--text-dim)", fontStyle: "italic", margin: 0, fontSize: 13 }}>
          No tags yet.
        </p>
      );
    }
    return (
      <div className="recipe-detail-tags">
        {tags.map((t) => <span key={t} className="recipe-detail-tag">#{t}</span>)}
      </div>
    );
  }
  const toggle = (t: string) => {
    const set = new Set(tags);
    if (set.has(t)) set.delete(t);
    else {
      if (set.size >= 5) return;
      set.add(t);
    }
    onChange([...set]);
  };
  const Row = ({ label, opts }: { label: string; opts: string[] }) => (
    <div className="recipe-detail-tag-row">
      <div className="recipe-detail-tag-row-label">{label}</div>
      <div className="recipe-detail-tag-row-chips">
        {opts.map((t) => (
          <button
            key={t}
            type="button"
            className={`recipe-detail-tag-pick${tags.includes(t) ? " active" : ""}`}
            onClick={() => toggle(t)}
            disabled={!tags.includes(t) && tags.length >= 5}
          >
            #{t}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="recipe-detail-tag-editor">
      <Row label="When" opts={RECIPE_TAG_GROUPS.time} />
      <Row label="Cuisine" opts={RECIPE_TAG_GROUPS.cuisine} />
      <Row label="Dish" opts={RECIPE_TAG_GROUPS.dish} />
      <div className="recipe-detail-tag-hint">
        Up to 5 tags · {5 - tags.length} remaining
      </div>
    </div>
  );
}
